import { useEffect, useRef } from 'react';
import { useCopyToClipboard as useCopyToClipboardBase } from 'react-use';

/**
 * A custom React hook that provides a simple interface for copying a value to the clipboard over react-use's useCopyToClipboard hook.
 *
 * @param {object} [options] - Optional configuration options.
 * @param {function(string)} [options.onSuccess] - Callback function to be called when the copy operation is successful.
 * @param {function(Error)} [options.onFailure] - Callback function to be called when the copy operation fails.
 * @returns {function(string)} - A function that can be called to attempt to copy a value to the clipboard.
 */
export const useCopyToClipboard = ({
  onSuccess,
  onFailure,
}: {
  onSuccess?: (copyValue: string) => void;
  onFailure?: (copyError: Error) => void;
}) => {
  // Grab the entire 'copyState' rather than just { error, value }
  const [copyState, onAttemptCopy] = useCopyToClipboardBase();

  // Use a ref to store the last copyState object we saw.
  const prevCopyRef = useRef(copyState);

  useEffect(() => {
    // If there's an error, call onFailure.
    if (copyState.error) {
      console.error(copyState.error);
      onFailure?.(copyState.error);
      // Update the ref and return early so we don't also trigger onSuccess.
      prevCopyRef.current = copyState;
      return;
    }

    // If `copyState.value` is truthy, and the reference changed,
    // then it's a new successful copy event.
    const isNewObjectRef = copyState !== prevCopyRef.current;
    if (copyState.value && isNewObjectRef) {
      onSuccess?.(copyState.value);
    }

    // Always update the ref after the effect runs.
    prevCopyRef.current = copyState;
  }, [copyState, onSuccess, onFailure]);

  return onAttemptCopy;
};
